import { useMemo } from 'react';
import { useInView } from 'react-intersection-observer';

import classNames from 'classnames';

import type { GridTableHeaderItem} from '@uikit';
import { GridTable, Spinner } from '@uikit';

import type { AutoLayerCampaignParticipant } from '@api';

import { useAuth } from '@shared/common/providers/AuthProvider';
import truncate from '@shared/helpers/web3/truncate';

import { useAutoLayerParticipantListInfiniteQuery, useSelfAutoLayerParticipant } from '@query/story';

export const formatPoints = (points: number) => {
  return new Intl.NumberFormat('en', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    //eslint-disable-next-line
    //@ts-ignore
    roundingMode: 'trunc',
  }).format(points);
};

const tableHeader: GridTableHeaderItem<
  AutoLayerCampaignParticipant,
  { self?: AutoLayerCampaignParticipant }
>[] = [
  {
    label: 'Rank',
    name: 'position',
    render: ({ item }) => item.attributes.position,
  },
  {
    label: 'Address',
    name: 'address',
    render: ({ item }) => truncate(item.id),
  },
  {
    label: 'Zealy Points',
    name: 'soQuestPoints',
    render: ({ item }) => formatPoints(item.attributes.soQuestPoints),
  },
  {
    label: 'AutoLayer Points',
    name: 'autoLayerPoints',
    render: ({ item }) => formatPoints(item.attributes.autoLayerPoints),
  },
  {
    label: 'Total Points',
    name: 'totalPoints',
    render: ({ item }) => formatPoints(item.attributes.totalPoints),
  },
];

const AutoLayerStoryLeaderboardTable = ({ className }: { className?: string }) => {
  const { isAuth } = useAuth();
  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useAutoLayerParticipantListInfiniteQuery();
  const { data: selfParticipant } = useSelfAutoLayerParticipant({
    enabled: isAuth,
  });

  const { ref } = useInView({
    onChange: (inView) => inView && !isLoading && !isFetchingNextPage && fetchNextPage(),
  });

  const allParticipants = useMemo(() => {
    const participants = data?.pages.flatMap((page) => page) || [];

    if (!selfParticipant?.id) {
      return participants;
    }

    const hasSelfParticipant = !!participants.find(({ id }) => id === selfParticipant.id);

    if (!hasSelfParticipant) {
      participants.push(selfParticipant);
    }

    return participants;
  }, [data, selfParticipant]);

  const showLoader = isLoading || hasNextPage;
  const showEmpty = !isLoading && !allParticipants?.length;

  return (
    <GridTable
      header={tableHeader}
      className={classNames(
        'overflow-y-auto flex flex-col rounded-t-2xl bg-neutral-50 shadow-card max-h-80 min-h-60',
        className,
      )}
      bodyRowClassName={({ item }) =>
        classNames('border-b border-primary-300 min-h-14', {
          'sticky top-0 lg:top-10 bg-secondary-50 -order-1':
            selfParticipant && selfParticipant?.id === item.id,
        })
      }
      meta={{
        self: selfParticipant,
      }}
      headRowClassName="bg-primary-200 text-neutral-500 sticky -order-1 top-0 max-lg:hidden"
      rowClassName="px-6 grid-cols-[100px_1.5fr] gap-1.5 min-w-min flex-shrink-0 h-10 items-center"
      data={allParticipants}
    >
      {showEmpty && (
        <p className="m-auto text-center text-neutral-200 text-sm">Leaderboard is Empty</p>
      )}

      {showLoader && (
        // Key is added to trigger in view cb again if component is already in view
        <div ref={ref} key={data?.pages.length} className="m-auto py-3">
          <Spinner size="md" />
        </div>
      )}
    </GridTable>
  );
};

export default AutoLayerStoryLeaderboardTable;
